// ⛔ 22 Sep 2026, Jacob: "i cant have this happen [BSCs silently unable to work, and nobody told
// until someone complains]." Before this, a broken interaction anywhere in the app was invisible
// until a person noticed and said something — exactly what happened with the lead-card click bug
// (#1263). This reports an unhandled error to the backend, which pages an admin the first time a
// new one shows up (see apps/api's clientErrorAlarm.logic.ts) — the same in-app alert channel
// every other alert already uses, no new vendor.
//
// ⛔ MUST NEVER THROW, AND MUST NEVER LOOP. A reporter that can itself crash, or that reports its
// own report failing, is how one broken thing becomes two. Every call here is fire-and-forget and
// wrapped so a network failure (or being offline) is silently swallowed, and a hard per-session cap
// stops a tight error loop (e.g. inside a render loop) from flooding the network tab or the server.
import { api } from '../api/client';

const MAX_REPORTS_PER_SESSION = 20;
let reportCount = 0;

export function reportError(error: unknown, context?: string): void {
  if (reportCount >= MAX_REPORTS_PER_SESSION) return;
  reportCount++;

  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';
  const stack = error instanceof Error ? error.stack ?? null : null;

  api('/api/alerts/client-error', {
    method: 'POST',
    body: JSON.stringify({
      message: context ? `${message} (${context})` : message,
      stack,
      url: window.location.pathname,
    }),
  }).catch(() => {
    /* best-effort — offline, logged out, or the endpoint itself is down; nothing more to do here */
  });
}

let installed = false;

/** Wires the two global catch-alls that a React error boundary cannot reach: a throw inside an
 *  ordinary event handler (not a render), and an unhandled promise rejection (a fetch nobody
 *  .catch()ed). Idempotent — safe to call once at startup. */
export function installGlobalErrorReporting(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    reportError(event.error ?? event.message, 'window.onerror');
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportError(event.reason, 'unhandledrejection');
  });
}
